/* The shell every ECHOE page shares: the tube, the warm-up, the footage,
   input, the cookie notice and the frame loop. The screen painted into the
   tube is picked by data-screen on <html>. Imports resolve through the
   import map in layout/theme.liquid, to each asset's versioned URL. */

import { Tube } from "./crt.js";
import { asset } from "./asset.js";
import { consent, known, decide as record } from "./consent.js";

const root = document.documentElement;
const SCREENS = ["home", "concerts", "tours", "catalog", "media", "policy"];
const screenName = SCREENS.includes(root.dataset.screen) ? root.dataset.screen : "home";
const { Screen } = await import(`./${screenName}.js`);

// The catalog turns the raster down so the posters stay sharp. A screen can
// also set its own `raster`, and change it as it goes.
const RASTER = screenName === "catalog" ? 0.22 : 1;

// Tube curvature for a 16:9 face. Larger is flatter.
const CURVE_REF = [4.0, 2.9];
const ASPECT_REF = 16 / 9;

// How far the bulge pushes the corners out, per axis; sampling is pulled back by this.
function overscanFor(curve) {
  return [1 / (1 + 1 / (curve[0] * curve[0])), 1 / (1 + 1 / (curve[1] * curve[1]))];
}

// Render size cap on the long and short sides: a CRT is not sharp, and at
// 4K a scanline would vanish.
const MAX_W = 1700;
const MAX_H = 1000;

// A phone gets flat glass: the bulge and the shaded corners suit a desk-sized
// screen, and on a phone they crowd the content and the controls. A phone is
// a window under PHONE CSS px on its shorter side, or a touch screen that
// small even when it is showing the desktop site. Glass standing upright is
// flat too, since the upright layouts run right to its edges.
const PHONE = 600;      // CSS px, shorter side
const coarse = matchMedia("(pointer: coarse)");
const phoneScreen = () => coarse.matches && Math.min(screen.width, screen.height) < PHONE;

const BLINK = 0.5333;              // 32 frames at 60Hz
const VIDEO_ASPECT = 854 / 480;

const canvas = document.getElementById("tube");
const video = document.getElementById("bg");
const navEl = document.getElementById("nav");
const fxBtn = document.getElementById("fxbtn");
const cookieEl = document.getElementById("cookie");
const analyticsBox = document.getElementById("cookie-analytics");
const policyLink = document.getElementById("cookie-policy");

const tube = new Tube(canvas);
const ui = new Screen();
const live = () => tube.ok && !tube.lost;

// a restored context comes back with empty textures
tube.onRestore = () => {
  for (const r of reels) { r.has = false; r.stillUp = false; r.fresh = true; }
  resize();
  ui.dirty = true;
};

// the inline script in <head> guessed; correct it if the context failed
root.classList.toggle("gl", tube.ok);

/* ---------------------------- state ---------------------------- */

const reduceQuery = matchMedia("(prefers-reduced-motion: reduce)");

const S = {
  motion: !reduceQuery.matches,
  flat: false,          // phone: no bulge
  css: [1, 1],          // the glass in CSS px
  hovered: null,        // hotspot id under the pointer
  focused: null,        // hotspot id holding keyboard focus
  fxHot: false,
  fxFocus: false,
  hint: null,
  tearAmp: 0,
  tearY: 0.5,
  bootStart: 0,
  fit: [1, 1],
  curve: CURVE_REF.slice(),
  overscan: overscanFor(CURVE_REF),
  scanCount: 420,
  activating: null,
  blocked: false,
  analytics: false,     // the analytics box, ticked or not
  choice: null,         // the saved choice: true, false, or null for none yet
  dlgHot: null,
  dlgFocus: null,
};

// The warm-up runs off the wall clock, so a tab opened in the background
// still finishes it.
S.bootStart = S.motion ? performance.now() / 1000 : -1e4;

const selected = () => (S.activating ? S.activating.id : S.focused !== null ? S.focused : S.hovered);
const hotEl = (id) => document.querySelector('[data-hot="' + id + '"]');

/* ---------------------------- consent ----------------------------
   The notice comes up until the visitor makes a choice, then stays away
   (consent.js keeps the choice, with Shopify). Nothing optional is set
   until a choice allows it, and closing the notice counts as Reject All.
   COOKIES beneath FX on the home screen leads to the cookie policy page (a
   screen with `settings`), which shows these controls itself, so a choice
   can be changed at any time. */

const stateEl = document.getElementById("cookie-state");   // the policy page's status line

/** Writes the saved choice into the policy page's status line. */
let savedTimer = 0;
function showChoice(justSaved) {
  if (!stateEl) return;
  const now = S.choice === null ? "No choice saved yet. Analytics cookies are off."
    : `Analytics cookies are ${S.choice ? "on" : "off"}.`;
  stateEl.textContent = (justSaved ? "Saved. " : "") + now;
  clearTimeout(savedTimer);
  if (justSaved) savedTimer = setTimeout(() => showChoice(false), 4000);
  ui.dirty = true;
}

function takeChoice() {
  S.choice = consent.choice;
  S.analytics = S.choice === true;
  analyticsBox.checked = S.analytics;
  showChoice(false);
  // a visitor who has chosen is not asked again, so the notice is not there at all
  if (S.choice !== null && !ui.settings) cookieEl.hidden = true;
}
takeChoice();
known.then(takeChoice);

function decide(analytics) {
  record(analytics);
  S.analytics = S.choice = analytics;
  analyticsBox.checked = analytics;
  showChoice(true);
  ui.dirty = true;
  if (ui.settings) return;
  ui.dialog.open = false;
  S.dlgHot = S.dlgFocus = null;
  cookieEl.hidden = true;
  setCursor("default");
}

/* ---------------------------- layout ---------------------------- */

/* Everything the layout depends on. It is checked every frame as well as on
   events, because a browser can change one without firing any: the pointer
   when a phone is detected, the pixel density when a window moves screens,
   a size that settles a frame after a phone turns. */
const zoom = () => Math.min(1, (window.visualViewport && window.visualViewport.scale) || 1);
const viewKey = () => [innerWidth, innerHeight, devicePixelRatio, coarse.matches, zoom(), screen.width, screen.height].join();
let laidOut = "";

function onView() {
  if (viewKey() !== laidOut) resize();
}

function resize() {
  laidOut = viewKey();
  const cssW = Math.max(1, canvas.clientWidth || innerWidth), cssH = Math.max(1, canvas.clientHeight || innerHeight);
  S.css = [cssW, cssH];
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  let bw = cssW * dpr;
  let bh = cssH * dpr;
  const k = Math.min(1, MAX_W / Math.max(bw, bh), MAX_H / Math.min(bw, bh));
  bw = Math.max(2, Math.round(bw * k));
  bh = Math.max(2, Math.round(bh * k));

  const sa = bw / bh;
  S.fit = sa > VIDEO_ASPECT ? [1, VIDEO_ASPECT / sa] : [sa / VIDEO_ASPECT, 1];
  S.flat = Math.min(cssW, cssH) < PHONE || phoneScreen() || cssH > cssW;
  if (S.flat) {
    S.curve = [1e6, 1e6];
    S.overscan = [1, 1];
  } else {
    // flatten the glass as the window gets further from 16:9
    const soften = Math.min(1, ASPECT_REF / (Math.max(bw, bh) / Math.min(bw, bh)));
    S.curve = [CURVE_REF[0] / soften, CURVE_REF[1] / soften];
    S.overscan = overscanFor(S.curve);
  }
  S.scanCount = Math.max(200, Math.min(640, bh / 2.4));

  // the part of the canvas the curved glass shows, measured along the edges
  const sx = Math.round((bw * (1 - S.overscan[0])) / 2);
  const sy = Math.round((bh * (1 - S.overscan[1])) / 2);
  tube.resize(bw, bh);
  // `unit` is canvas px per CSS px as the eye sees them, for sizing type and
  // touch targets: a phone showing the desktop site is zoomed out, so its
  // CSS px are smaller
  ui.resize(bw, bh, { x: sx, y: sy, w: bw - sx * 2, h: bh - sy * 2 }, { unit: bw / cssW / zoom(), touch: coarse.matches });
  ui.dirty = true;
  // a field being typed in stays on the glass
  const a = document.activeElement;
  if (ui.reveal && a && a.matches && a.matches("input, textarea")) ui.reveal(a);
}

for (const [target, type] of [[window, "resize"], [window, "orientationchange"], [window, "pageshow"], [coarse, "change"]]) {
  target.addEventListener(type, onView, { passive: true });
}

/* ---------------------------- footage ----------------------------
   The concerts page has one reel per show, each in its own texture, and the
   two on the glass are stacked by the shader. Only those two play; the
   ones either side preload metadata. Distant reels release their downloads
   and decoders so they cannot delay the footage coming onto the glass. */

const RVFC = "requestVideoFrameCallback" in HTMLVideoElement.prototype;
const reels = [];

function addReel(el, spec) {
  const r = {
    i: reels.length, el,
    source: el.src,
    resume: 0,
    hold: spec ? spec.hold : 8,   // the frame FX OFF rests on
    want: false,                  // on the glass, so it should play
    fresh: false,                 // a new frame is waiting
    has: false,                   // the tube holds a real frame
    still: null,                  // shown until the reel has a frame
    stillUp: false,
  };
  if (RVFC) {
    const pump = () => { r.fresh = true; el.requestVideoFrameCallback(pump); };
    el.requestVideoFrameCallback(pump);
  } else {
    el.addEventListener("timeupdate", () => { r.fresh = true; });
  }
  el.addEventListener("loadeddata", () => { r.fresh = true; });
  el.addEventListener("loadedmetadata", () => {
    if (r.resume > 0) {
      try { el.currentTime = r.resume; } catch (_) {}
      r.resume = 0;
    }
  });
  el.addEventListener("seeked", () => { r.fresh = true; });
  // nothing plays while effects are off or while its show is off the glass
  el.addEventListener("play", () => { if (!S.motion || !r.want) el.pause(); });
  el.addEventListener("playing", () => {
    S.blocked = false;
    if (S.hint) { S.hint = null; ui.dirty = true; }
  });
  if (spec && spec.still) {
    const img = new Image();
    img.decoding = "async";
    img.crossOrigin = "anonymous";
    img.addEventListener("load", () => { r.still = img; });
    img.src = spec.still;
  }
  reels.push(r);
  return r;
}

const view = () => (ui.reelView ? ui.reelView() : { a: 0, b: 0, t: 0 });
const onGlass = (r, v) => r.i === v.a || (v.t > 0 && r.i === v.b);

function wake(r) {
  if (!S.motion) { rest(r); return; }
  if (r.el.preload !== "auto") r.el.preload = "auto";
  if (document.hidden) return;   // resumed on visibilitychange
  const p = r.el.play();
  if (p && p.catch) {
    p.catch((err) => {
      if (err && err.name === "NotAllowedError") {
        S.blocked = true;
        S.hint = coarse.matches ? "TAP TO PLAY" : "PRESS ANY KEY";
        ui.dirty = true;
      }
    });
  }
}

// FX OFF holds a reel still; one that never played rests on its chosen frame
function rest(r) {
  r.el.pause();
  if (r.el.currentTime > 0.05 || r.resume > 0.05) return;
  const seek = () => { r.el.pause(); try { r.el.currentTime = r.hold; } catch (_) {} };
  if (r.el.readyState >= 1) { seek(); return; }
  r.el.addEventListener("loadedmetadata", seek, { once: true });
  if (r.el.preload === "none") {
    r.el.preload = "metadata";
    try { r.el.load(); } catch (_) {}
  }
}

function steerReels(v) {
  for (const r of reels) {
    const want = onGlass(r, v);
    if (!want && Math.abs(r.i - v.a) > 1) {
      r.want = false;
      if (r.el.hasAttribute("src")) {
        r.resume = r.el.currentTime || r.resume;
        r.el.pause();
        r.el.removeAttribute("src");
        r.el.load();
      }
      continue;
    }
    if (!r.el.hasAttribute("src")) {
      r.el.preload = want ? "auto" : "metadata";
      r.el.src = r.source;
    }
    if (want !== r.want) {
      r.want = want;
      if (want) { if (!S.blocked || !S.motion) wake(r); }
      else { r.el.pause(); r.el.preload = "metadata"; }
    }
    if (!want && Math.abs(r.i - v.a) <= 1 && r.el.preload === "none") {
      r.el.preload = "metadata";
      try { r.el.load(); } catch (_) {}
    }
  }
}

function feedReels(v) {
  for (const r of reels) {
    if (onGlass(r, v) && r.fresh && r.el.readyState >= 2) {
      if (tube.uploadReel(r.i, r.el)) r.has = true;
      if (RVFC) r.fresh = false;
    }
    // a still goes up as soon as it loads, so no show scrolls in with another's footage
    if (!r.has && r.still && !r.stillUp) {
      tube.uploadReel(r.i, r.still);
      r.stillUp = true;
    }
  }
}

function start() {
  const v = view();
  for (const r of reels) {
    r.want = onGlass(r, v);
    if (r.want) wake(r); else r.el.pause();
  }
}

document.addEventListener("visibilitychange", () => {
  if (document.hidden) { for (const r of reels) r.el.pause(); }
  else if (S.motion && !S.blocked) {
    for (const r of reels) if (r.want) r.el.play().catch(() => {});
  }
});

// pointerup is the gesture a touch screen lets start playback
function firstGesture() { if (S.blocked && S.motion) start(); }
addEventListener("pointerdown", firstGesture);
addEventListener("pointerup", firstGesture);
addEventListener("keydown", firstGesture);

/* ---------------------------- pointer ---------------------------- */

// Redoes the shader's warp, so the pointer picks what is drawn under it.
function toScreen(clientX, clientY) {
  const r = canvas.getBoundingClientRect();
  let cx = ((clientX - r.left) / r.width) * 2 - 1;
  let cy = ((clientY - r.top) / r.height) * 2 - 1;
  const wx = Math.abs(cy) / S.curve[0];
  const wy = Math.abs(cx) / S.curve[1];
  cx += cx * wx * wx;
  cy += cy * wy * wy;
  cx *= S.overscan[0];
  cy *= S.overscan[1];
  return [(cx * 0.5 + 0.5) * ui.w, (cy * 0.5 + 0.5) * ui.h];
}

// the pointer, in client px, while it is over the glass
let pointer = null;

function setCursor(c) {
  if (canvas.style.cursor !== c) canvas.style.cursor = c;
}

/** What is under the pointer. Run on every move and every frame, so the
    hover follows the page when it changes under a still pointer. */
function hover() {
  if (!pointer) return;
  const [x, y] = toScreen(pointer.x, pointer.y);
  const hit = ui.pick(x, y);

  if (hit && hit.kind === "dialog") {
    const id = hit.id === "outside" || hit.id === "chrome" ? null : hit.id;
    if (id !== S.dlgHot || S.hovered !== null || S.fxHot) {
      S.dlgHot = id;
      S.hovered = null;
      S.fxHot = false;
      ui.dirty = true;
    }
    setCursor(id ? "pointer" : "default");
    return;
  }

  const id = hit && (hit.kind === "hot" || hit.kind === "field") ? hit.id : null;
  const fxHot = !!hit && hit.kind === "fx";
  if (id !== S.hovered || fxHot !== S.fxHot || S.dlgHot) {
    S.hovered = id;
    S.fxHot = fxHot;
    S.dlgHot = null;
    ui.dirty = true;
  }
  setCursor(!hit ? "default" : hit.cursor || (hit.text ? "text" : "pointer"));
}

canvas.addEventListener("pointermove", (e) => {
  if (e.pointerType !== "mouse") { touchMove(e); return; }
  pointer = { x: e.clientX, y: e.clientY };
  hover();
}, { passive: true });

canvas.addEventListener("pointerleave", () => {
  pointer = null;
  if (S.dlgHot || S.hovered !== null || S.fxHot) {
    S.dlgHot = null;
    S.hovered = null;
    S.fxHot = false;
    ui.dirty = true;
  }
  setCursor("default");
}, { passive: true });

/* Touch: a finger drags the page and a flick carries on; a sideways swipe
   steps an open poster to the next one. Taps still arrive as clicks, and a
   drag is never also a tap. */
let touch = null;
let eatClick = false;

canvas.addEventListener("pointerdown", (e) => {
  if (e.pointerType === "mouse") return;
  // a finger landing on a page still moving stops it, and is not a tap
  eatClick = !ui.dialog.open && !!ui.hold && ui.hold();
  touch = { id: e.pointerId, x0: e.clientX, y0: e.clientY, y: e.clientY, axis: null, trail: [[e.timeStamp, e.clientY]] };
}, { passive: true });

function touchMove(e) {
  const T = touch;
  if (!T || e.pointerId !== T.id) return;
  const dx = e.clientX - T.x0, dy = e.clientY - T.y0;
  if (!T.axis && Math.hypot(dx, dy) > 8) T.axis = Math.abs(dy) >= Math.abs(dx) ? "y" : "x";
  if (T.axis === "y" && ui.drag && !ui.dialog.open) ui.drag((T.y - e.clientY) * (ui.h / S.css[1]));
  T.y = e.clientY;
  T.trail.push([e.timeStamp, e.clientY]);
  while (T.trail.length > 2 && e.timeStamp - T.trail[0][0] > 100) T.trail.shift();
}

canvas.addEventListener("pointerup", (e) => {
  const T = touch;
  if (!T || e.pointerId !== T.id) return;
  touch = null;
  if (!T.axis) return;
  eatClick = true;
  if (ui.dialog.open) return;
  if (T.axis === "y" && ui.fling) {
    const [t0, y0] = T.trail[0];
    const dt = (e.timeStamp - t0) / 1000;
    const v = dt > 0.008 && e.timeStamp - T.trail[T.trail.length - 1][0] < 80 ? (y0 - e.clientY) / dt : 0;
    ui.fling(v * (ui.h / S.css[1]));
  } else if (T.axis === "x" && ui.swipe && Math.abs(e.clientX - T.x0) > 40) {
    ui.swipe(e.clientX < T.x0 ? 1 : -1);
  }
}, { passive: true });

canvas.addEventListener("pointercancel", () => { touch = null; }, { passive: true });

canvas.addEventListener("click", (e) => {
  if (eatClick) { eatClick = false; return; }
  const [x, y] = toScreen(e.clientX, e.clientY);
  const hit = ui.pick(x, y);
  if (!hit) return;
  if (hit.kind === "dialog") { onDialog(hit.id, e); return; }
  if (hit.kind === "fx") { toggleFx(); return; }
  // straight through, so a file picker opens inside the click that asked for it
  if (hit.kind === "field") { ui.focusField(hit.id, x, y); return; }
  activate(hit.id, e);
});

if (ui.wheel) {
  canvas.addEventListener("wheel", (e) => {
    if (e.ctrlKey) return;   // pinch and ctrl-scroll zoom the page
    e.preventDefault();
    if (ui.dialog.open || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    let d = e.deltaY;
    if (e.deltaMode === 1) d *= 16;
    else if (e.deltaMode === 2) d *= S.css[1];
    ui.wheel(d * (ui.h / S.css[1]));
  }, { passive: false });
}

/* ---------------------------- keyboard ---------------------------- */

if (ui.key) {
  addEventListener("keydown", (e) => {
    if (e.defaultPrevented || ui.dialog.open || e.altKey || e.ctrlKey || e.metaKey) return;
    const el = e.target;
    if (el.closest && el.closest("input, textarea, select")) return;
    if (e.key === " " && el.closest && el.closest("a, button")) return;
    if (ui.key(e.key, e.shiftKey)) e.preventDefault();
  });
}

// pages made of named things can be linked into by name
let firstJump = -1;
if (ui.indexOf) {
  const fromHash = () => ui.indexOf(decodeURIComponent(location.hash.slice(1)));
  addEventListener("hashchange", () => {
    const i = fromHash();
    if (i >= 0) ui.jump(i);
  });
  // replaced, not pushed, so Back leaves the page rather than stepping through it
  ui.onPlace = (slug) => {
    if (decodeURIComponent(location.hash.slice(1)) === (slug || "")) return;
    try {
      history.replaceState(history.state, "", slug ? "#" + slug : location.pathname + location.search);
    } catch (_) {}
  };
  firstJump = fromHash();
}

if (navEl) {
  navEl.addEventListener("keydown", (e) => {
    const links = [...navEl.querySelectorAll("a")];
    const i = links.indexOf(document.activeElement);
    if (i < 0) return;
    let next = -1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % links.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (i - 1 + links.length) % links.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = links.length - 1;
    if (next >= 0) { e.preventDefault(); links[next].focus(); }
  });
}

/* ------------------------- activation -------------------------
   A link flashes before it navigates. The real anchor is what navigates,
   so new tabs, the keyboard and screen readers all behave. */

let committing = false;

function commit(link) {
  committing = true;
  try { link.click(); } finally { committing = false; }
}

function activate(id, evt) {
  if (ui.act && ui.act(id)) { ui.dirty = true; return; }
  const link = hotEl(id);
  if (!link) return;
  if (evt && (evt.metaKey || evt.ctrlKey || evt.shiftKey)) {
    window.open(link.href, "_blank", "noopener");
    return;
  }
  if (!S.motion) { commit(link); return; }
  if (!S.activating) S.activating = { id, t: 0 };
}

// the keyboard and assistive tech click the real anchors
document.addEventListener("click", (e) => {
  if (committing || !live()) return;
  const link = e.target.closest && e.target.closest("[data-hot]");
  if (!link || e.metaKey || e.ctrlKey || e.shiftKey) return;
  const id = link.dataset.hot;
  if (ui.act && ui.act(id)) { e.preventDefault(); ui.dirty = true; return; }
  if (!S.motion || S.activating) return;
  e.preventDefault();
  S.activating = { id, t: 0 };
});

document.addEventListener("focusin", (e) => {
  const el = e.target.closest && e.target.closest("[data-hot]");
  const id = el ? el.dataset.hot : null;
  if (id !== S.focused) { S.focused = id; ui.dirty = true; }
  if (ui.reveal && live()) ui.reveal(e.target);
});
document.addEventListener("focusout", () => {
  setTimeout(() => {
    const el = document.activeElement;
    if (!(el && el.closest && el.closest("[data-hot]")) && S.focused !== null) {
      S.focused = null;
      ui.dirty = true;
    }
  }, 0);
});

function syncFxControl() {
  fxBtn.setAttribute("aria-pressed", String(!S.motion));
  if (screenName === "home") fxBtn.textContent = S.motion ? "FX ON" : "FX OFF";
}

function toggleFx() {
  S.motion = !S.motion;
  syncFxControl();
  if (S.motion) { S.bootStart = -1e4; start(); }
  else {
    for (const r of reels) r.el.pause();
    S.tearAmp = 0;
    S.hint = null;
  }
  ui.dirty = true;
}

fxBtn.addEventListener("click", (e) => { e.preventDefault(); toggleFx(); });
fxBtn.addEventListener("focus", () => { S.fxFocus = true; ui.dirty = true; });
fxBtn.addEventListener("blur", () => { S.fxFocus = false; ui.dirty = true; });

reduceQuery.addEventListener("change", (e) => {
  S.motion = !e.matches;
  syncFxControl();
  if (S.motion) start(); else for (const r of reels) r.el.pause();
  ui.dirty = true;
});

/* ------------------------ the cookie notice ------------------------ */

function onDialog(id, e) {
  switch (id) {
    case "accept": decide(true); break;
    case "save": decide(S.analytics); break;
    case "reject":
    case "close": decide(false); break;
    case "check":
      S.analytics = !S.analytics;
      analyticsBox.checked = S.analytics;
      ui.dirty = true;
      break;
    case "link":
      if (e.metaKey || e.ctrlKey || e.shiftKey) window.open(policyLink.href, "_blank", "noopener");
      else policyLink.click();
      break;
    default: break;
  }
}

cookieEl.addEventListener("click", (e) => {
  const btn = e.target.closest("button[data-consent]");
  if (!btn) return;
  const k = btn.dataset.consent;
  decide(k === "accept" ? true : k === "reject" ? false : analyticsBox.checked);
});

analyticsBox.addEventListener("change", () => {
  S.analytics = analyticsBox.checked;
  ui.dirty = true;
});

function dialogControlId(el) {
  if (el === analyticsBox) return "check";
  if (el === policyLink) return "link";
  const b = el && el.closest && el.closest("button[data-consent]");
  return b ? b.dataset.consent : null;
}

cookieEl.addEventListener("focusin", (e) => {
  const id = dialogControlId(e.target);
  if (id !== S.dlgFocus) { S.dlgFocus = id; ui.dirty = true; }
});
cookieEl.addEventListener("focusout", () => {
  setTimeout(() => {
    if (!cookieEl.contains(document.activeElement) && S.dlgFocus) {
      S.dlgFocus = null;
      ui.dirty = true;
    }
  }, 0);
});

// Escape closes the notice, or the catalog's viewer; Tab stays inside the notice
addEventListener("keydown", (e) => {
  if (!ui.dialog.open) {
    if (e.key === "Escape" && ui.escape && ui.escape()) e.preventDefault();
    return;
  }
  if (e.key === "Escape") { e.preventDefault(); decide(false); return; }
  if (e.key !== "Tab") return;
  const f = [...cookieEl.querySelectorAll("a[href], button, input")];
  const first = f[0], last = f[f.length - 1];
  if (!cookieEl.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
  else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
}, true);

/* ---------------------------- warm-up ----------------------------
   A line strikes across, the raster opens out, the signal settles. */

const BOOT_END = 1.0;

function warmUp(bt) {
  if (bt >= BOOT_END) return { open: 1, lineW: 1.2, flash: 0, noise: 0 };
  if (bt < 0.18) {
    const e = 1 - Math.pow(1 - bt / 0.18, 3);
    return { open: 0.004, lineW: e * 1.05, flash: 0.30 * e, noise: 0.04 };
  }
  if (bt < 0.26) {
    return { open: 0.004, lineW: 1.2, flash: 0.30 + 0.24 * ((bt - 0.18) / 0.08), noise: 0.05 };
  }
  if (bt < 0.62) {
    const p = (bt - 0.26) / 0.36;
    const e = 1 - Math.pow(1 - p, 3);
    return { open: 0.004 + e * 0.996, lineW: 1.2, flash: 0.54 * Math.pow(1 - p, 1.6), noise: 0.26 * (1 - p * 0.4) };
  }
  const p = (bt - 0.62) / 0.38;
  return { open: 1, lineW: 1.2, flash: 0, noise: 0.16 * Math.pow(1 - p, 2) };
}

/* ---------------------- occasional bad signal ---------------------- */

let nextTear = 6 + Math.random() * 7;
let tear = null;

function signal(t, dt) {
  if (!S.motion) { S.tearAmp = 0; return; }
  if (!tear && t > nextTear) {
    tear = {
      t: 0,
      dur: 0.09 + Math.random() * 0.13,
      amp: (Math.random() < 0.5 ? -1 : 1) * (0.005 + Math.random() * 0.013),
    };
    S.tearY = 0.12 + Math.random() * 0.76;
  }
  if (tear) {
    tear.t += dt;
    const p = tear.t / tear.dur;
    if (p >= 1) {
      tear = null;
      S.tearAmp = 0;
      nextTear = t + 7 + Math.random() * 10;
    } else {
      S.tearAmp = tear.amp * Math.sin(Math.PI * p);
    }
  }
}

/* ---------------------------- loop ---------------------------- */

let last = performance.now() / 1000;
let prevBlink = true;
let prevSel = " ";

function frame(now) {
  requestAnimationFrame(frame);
  if (!live()) return;
  onView();

  const t = now / 1000;
  const dt = Math.min(0.05, Math.max(0, t - last));
  last = t;

  const boot = warmUp(t - S.bootStart);
  signal(t, dt);

  // the notice waits until the picture has come up
  if (consent.ready && S.choice === null && !ui.dialog.open && !ui.settings && t - S.bootStart > BOOT_END + 0.25) {
    ui.dialog.open = true;
    ui.dirty = true;
    const first = cookieEl.querySelector("button[data-consent]");
    if (first && !cookieEl.contains(document.activeElement)) first.focus({ preventScroll: true });
  }

  let blink = S.motion ? Math.floor(t / BLINK) % 2 === 0 : true;
  if (S.activating) {
    S.activating.t += dt;
    blink = Math.floor(S.activating.t / 0.05) % 2 === 0;
    if (S.activating.t > 0.4) {
      const link = hotEl(S.activating.id);
      S.activating = null;
      if (link) { commit(link); return; }
    }
  }

  const sel = selected();
  if (blink !== prevBlink || sel !== prevSel) ui.dirty = true;
  prevBlink = blink;
  prevSel = sel;

  if (ui.tick && ui.tick(dt, t, S.motion)) ui.dirty = true;
  hover();

  const v = view();
  steerReels(v);
  feedReels(v);
  const seen = reels[v.a];

  // only the catalog's lamps have changed: re-send just their bands
  if (!ui.dirty && ui.patch) ui.patch((c, x, y) => tube.patchUI(c, x, y), t, S.motion);

  if (ui.dirty) {
    ui.paint({
      selected: sel, blink, t,
      fx: S.motion, fxHot: S.fxHot, fxFocus: S.fxFocus, hint: S.hint,
      hot: S.dlgHot !== null ? S.dlgHot : sel,
      focus: S.dlgFocus !== null ? S.dlgFocus : S.focused,
      analytics: S.analytics,
      choice: S.choice,
    });
    tube.uploadUI(ui.canvas);
  }

  tube.draw({
    time: t,
    curve: S.curve,
    overscan: S.overscan,
    fit: S.fit,
    scanCount: S.scanCount,
    open: boot.open,
    lineW: boot.lineW,
    flash: boot.flash,
    noise: boot.noise + (seen && !seen.has && !seen.stillUp ? 0.02 : 0),
    tearAmp: S.tearAmp,
    tearY: S.tearY,
    fx: S.motion ? 1 : 0.34,
    motion: S.motion ? 1 : 0,
    raster: ui.raster ?? RASTER,
    flat: S.flat ? 1 : 0,
    reelA: v.a,
    reelB: v.b,
    reel: v.t,
  });
}

/* ---------------------------- boot ---------------------------- */

// Everything the tube draws is fetched with CORS: Shopify serves assets
// with Access-Control-Allow-Origin, and a preview on another domain would
// otherwise taint the canvas that WebGL reads.
function loadImage(src, onLoad) {
  const img = new Image();
  img.decoding = "async";
  img.crossOrigin = "anonymous";
  img.addEventListener("load", () => { onLoad(img); ui.dirty = true; });
  img.src = src;
}

if (ui.shows) {
  ui.shows.forEach((show, i) => loadImage(show.poster, (img) => ui.setPoster(i, img)));
} else if (ui.gallery) {
  // four at a time, so the first screenful is not queued behind the rest
  let at = 0;
  const next = () => {
    if (at >= ui.gallery.length) return;
    const i = at++;
    loadImage(ui.gallery[i].poster, (img) => { ui.setPoster(i, img); next(); });
  };
  for (let k = 0; k < 4; k++) next();
} else if (ui.setLogo) {
  loadImage(asset("echoe-logo.png"), (img) => ui.setLogo(img));
}

// The first reel is the <video> in the page, so the page without the tube
// keeps its background. The rest exist only for the tube.
if (video) addReel(video, ui.shows ? ui.shows[0] : null);
if (video && ui.shows && tube.ok) {
  for (let i = 1; i < ui.shows.length; i++) {
    const el = document.createElement("video");
    el.className = "reel";
    el.muted = el.defaultMuted = true;
    el.loop = true;
    el.playsInline = true;
    el.setAttribute("muted", "");
    el.setAttribute("playsinline", "");
    el.setAttribute("aria-hidden", "true");
    el.tabIndex = -1;
    el.disablePictureInPicture = true;
    el.crossOrigin = "anonymous";
    el.preload = "none";
    el.src = ui.shows[i].reel;
    video.after(el);
    addReel(el, ui.shows[i]);
  }
}

syncFxControl();
resize();
if (firstJump >= 0) ui.jump(firstJump);
start();
requestAnimationFrame(frame);
